import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL, fetchUpdates } from '../api';
import { useAuth } from '../auth/useAuth';
import { REFRESH_INTERVAL_MS } from '../constants';
import { S } from '../strings';
import type { FeedSource, ParsedItem } from '../types';

export interface DashboardData {
  data: ParsedItem[];
  // Community (independent-blogger) items for the Yhteisö tab; [] until the
  // read flow sends them. Never part of `data`.
  community: ParsedItem[];
  // Feed-source listing from the read flow; [] when unavailable.
  sources: FeedSource[];
  loading: boolean;
  error: string | null;
  lastRefresh: Date | null;
  isLive: boolean;
  refresh: () => void;
  dismissError: () => void;
}

// Owns the /api/updates fetch, the auth-gated initial load, and the
// background refresh interval. Extracted from Dashboard.tsx so the shell
// can be re-rendered, filtered, and tested without dragging fetch plumbing
// along. See plan-the-h-6-dashboard-tsx-cheeky-swing.md § 2 (PR 1).
export function useDashboardData(): DashboardData {
  const [data, setData] = useState<ParsedItem[]>([]);
  const [community, setCommunity] = useState<ParsedItem[]>([]);
  const [sources, setSources] = useState<FeedSource[]>([]);
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const { account, acquireApiToken } = useAuth();

  // Hold acquireApiToken in a ref so `load` can stay stable. Otherwise the
  // callback's ref churns each time the Teams-SSO probe settles (inTeams /
  // teamsSsoReady transition null -> bool), refiring the mount effect and
  // producing a duplicate /api/* fetch per session.
  const acquireApiTokenRef = useRef(acquireApiToken);
  acquireApiTokenRef.current = acquireApiToken;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await acquireApiTokenRef.current();
      const {
        items,
        community: communityItems,
        sources: feedSources,
      } = await fetchUpdates(API_URL, token);
      if (items.length === 0) throw new Error(S.errorEmptyResponse);
      setData(items);
      setCommunity(communityItems);
      setSources(feedSources);
      setIsLive(true);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Gate on account so we don't fire a fetch while the auth probe is still
  // pending (account would be null => acquireApiToken throws "no account").
  useEffect(() => {
    if (!account) return;
    void load();
  }, [account, load]);

  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => {
      // Skip the refresh while the tab is in the background — saves Function
      // App invocations on idle browser sessions and avoids the dashboard
      // suddenly re-rendering when a user switches back.
      if (document.visibilityState !== 'visible') return;
      void load();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isLive, load]);

  const refresh = useCallback(() => {
    void load();
  }, [load]);

  const dismissError = useCallback(() => setError(null), []);

  return { data, community, sources, loading, error, lastRefresh, isLive, refresh, dismissError };
}
